import { useState, Suspense, useEffect } from "react";
import {
  Alert,
  Tabs,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";

import { ZSLTabContent } from "./ZSLTabContent";
import { CanvasTabContent } from "./CanvasTabContent";
import { AccountTabContent } from "./AccountTabContent";
import { StatesTabContent } from "./StatesTabContent";
import { FeatureFlagsTabContent } from "./FeatureFlagsTabContent";
import { DebugTabContent } from "./DebugTabContent";
import styles from "./DevTools.module.css";

const STORAGE_KEY = "devToolsSelectedTab";

const tabs = [
  {
    href: "#canvas",
    text: (
      <Tooltip content="Canvas" placement="top" offset="small">
        <TooltipFocusableTrigger>
          <Icon name="navCanvas" size={20} />
        </TooltipFocusableTrigger>
      </Tooltip>
    ),
  },
  {
    href: "#feature-flags",
    text: (
      <Tooltip content="Feature Flags" placement="top" offset="small">
        <TooltipFocusableTrigger>
          <Icon name="actionFlag" size={20} />
        </TooltipFocusableTrigger>
      </Tooltip>
    ),
  },
  {
    href: "#debug",
    text: (
      <Tooltip content="Debug" placement="top" offset="small">
        <TooltipFocusableTrigger>
          <Icon name="actionBug" size={20} />
        </TooltipFocusableTrigger>
      </Tooltip>
    ),
  },
  {
    href: "#account",
    text: (
      <Tooltip content="Account" placement="top" offset="small">
        <TooltipFocusableTrigger>
          <Icon name="personCircle" size={20} />
        </TooltipFocusableTrigger>
      </Tooltip>
    ),
  },
  {
    href: "#states",
    text: (
      <Tooltip content="States" placement="top" offset="small">
        <TooltipFocusableTrigger>
          <Icon name="miscEye" size={20} />
        </TooltipFocusableTrigger>
      </Tooltip>
    ),
  },
  {
    href: "#zsl",
    text: (
      <Tooltip content="ZSL & Template" placement="top" offset="small">
        <TooltipFocusableTrigger>
          <Icon name="navTemplate" size={20} />
        </TooltipFocusableTrigger>
      </Tooltip>
    ),
  },
];

const DevTools = () => {
  const [selectedTabHref, setSelectedTabHref] = useState<string>(() => {
    if (typeof window === "undefined") return "#canvas";
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored || "#canvas";
    } catch {
      return "#canvas";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, selectedTabHref);
    } catch (error) {
      console.error(
        "Failed to save selected DevTools tab to localStorage:",
        error,
      );
    }
  }, [selectedTabHref]);

  return (
    <div className={styles.devtoolsSectionWrapper}>
      <Alert
        title="DevTools are only available to Zapier staff"
        status="warning"
        variant="compact"
      />
      <Tabs
        items={tabs}
        onChooseItem={(item) => setSelectedTabHref(item.href)}
        selectedItemHref={selectedTabHref}
      />
      <div className={styles.tabContentWrapper}>
        <Suspense fallback={null}>
          {selectedTabHref === "#canvas" && <CanvasTabContent />}
          {selectedTabHref === "#feature-flags" && <FeatureFlagsTabContent />}
          {selectedTabHref === "#debug" && <DebugTabContent />}
          {selectedTabHref === "#states" && <StatesTabContent />}
          {selectedTabHref === "#account" && <AccountTabContent />}
          {selectedTabHref === "#zsl" && <ZSLTabContent />}
        </Suspense>
      </div>
    </div>
  );
};

export default DevTools;
